import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, Send } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data/content";
import DemoDialog from "@/components/DemoDialog";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

function Header({ onOpenDemo }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050816]/80 border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="group">
          <Logo size="md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-white bg-white/5" : "text-gray-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`}
            className="text-sm text-gray-300 hover:text-white transition-colors"
            data-testid="header-phone"
          >
            {COMPANY.phone}
          </a>
          <Button
            data-testid="header-book-demo"
            onClick={onOpenDemo}
            className="btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-full px-5 h-10"
          >
            Book a Demo
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="lg:hidden border-t border-white/10 bg-[#050816]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-md text-sm ${
                      isActive ? "text-white bg-white/5" : "text-gray-300 hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Button
                data-testid="mobile-book-demo"
                onClick={() => { setOpen(false); onOpenDemo(); }}
                className="mt-2 btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-full h-11"
              >
                Book a Demo
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer({ onOpenDemo }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Watch out for updates.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again later.");
    } finally { setLoading(false); }
  };

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#02040c]">
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Ready to launch?</div>
            <h3 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-none">
              Let's build your <span className="text-gradient-blue">iGaming</span> brand.
            </h3>
            <p className="mt-4 text-gray-400 leading-relaxed max-w-md">
              Talk to our solutions team — free consultation on licensing, tech stack and time-to-launch.
            </p>
            <Button
              data-testid="footer-book-demo"
              onClick={onOpenDemo}
              className="mt-6 btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-12 px-7 text-white"
            >
              Book a Free Demo
            </Button>

            <form onSubmit={submit} className="mt-10 max-w-md" data-testid="newsletter-form">
              <label className="overline">Subscribe for insights</label>
              <div className="mt-3 flex items-center gap-2 glass rounded-full p-1.5 pr-2">
                <input
                  type="email"
                  required
                  data-testid="newsletter-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-gray-500 px-4 h-10"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="rounded-full bg-white/10 hover:bg-white/20 text-white h-9 px-4"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-3">
            <div className="overline">Services</div>
            <ul className="mt-4 space-y-3 text-sm text-gray-400">
              <li><Link to="/services" className="hover:text-white">White Label Casino</Link></li>
              <li><Link to="/services" className="hover:text-white">White Label Sportsbook</Link></li>
              <li><Link to="/services" className="hover:text-white">Crypto Casino</Link></li>
              <li><Link to="/services" className="hover:text-white">Turnkey Platform</Link></li>
              <li><Link to="/services" className="hover:text-white">Game Aggregation</Link></li>
              <li><Link to="/services" className="hover:text-white">API Integration</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline">Company</div>
            <ul className="mt-4 space-y-3 text-sm text-gray-400">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/solutions" className="hover:text-white">Solutions</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/admin/login" className="hover:text-white">Admin</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline">Reach us</div>
            <ul className="mt-4 space-y-4 text-sm text-gray-400">
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#00D4FF]"/> {COMPANY.address}</li>
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#00D4FF]"/><a href={`tel:${COMPANY.phone}`} className="hover:text-white">{COMPANY.phone}</a></li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#00D4FF]"/><a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-gray-500">
          <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved. B2B iGaming software provider — for licensed operators only.</div>
          <div className="flex gap-4">
            <span>Privacy Policy</span>
            <span>Cookies</span>
            <span>Responsible Gaming</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FloatingCTAs({ onOpenDemo }) {
  return (
    <div className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 z-40 flex flex-col gap-3">
      <a
        href="https://wa.me/447577308890"
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp"
        className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white"><path d="M20.5 3.5A11 11 0 0 0 3.4 17l-1.4 5 5.1-1.4A11 11 0 1 0 20.5 3.5Zm-8.5 18a9 9 0 0 1-4.6-1.3l-.3-.2-3 .8.8-2.9-.2-.3A9 9 0 1 1 12 21.5Zm5.2-6.7c-.3-.1-1.7-.8-1.9-.9s-.4-.1-.6.1-.7.9-.8 1c-.2.2-.3.2-.6.1a7.4 7.4 0 0 1-2.2-1.4 8.2 8.2 0 0 1-1.5-1.9c-.2-.3 0-.5.1-.6l.4-.5c.1-.1.2-.2.2-.4s.1-.3 0-.5c0-.2-.6-1.4-.8-1.9s-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2c0 1.3.9 2.5 1 2.7s1.8 2.8 4.4 3.9c.6.3 1.1.4 1.5.5.6.2 1.2.2 1.6.1.5-.1 1.7-.7 1.9-1.4s.2-1.2.2-1.4c-.1-.2-.3-.2-.6-.3Z"/></svg>
      </a>
      <button
        data-testid="floating-book-demo"
        onClick={onOpenDemo}
        className="hidden sm:flex items-center gap-2 h-12 px-5 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-sm font-medium btn-glow"
      >
        Book a Demo
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const [demoOpen, setDemoOpen] = useState(false);
  const [prefill, setPrefill] = useState({});

  const openDemo = (service) => {
    setPrefill(service ? { service_interest: service } : {});
    setDemoOpen(true);
  };

  useEffect(() => {
    const handler = (e) => openDemo(e?.detail?.service);
    window.addEventListener("alphatech:open-demo", handler);
    return () => window.removeEventListener("alphatech:open-demo", handler);
  }, []);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <Header onOpenDemo={() => openDemo()} />
      <main className="pt-16">{children}</main>
      <Footer onOpenDemo={() => openDemo()} />
      <FloatingCTAs onOpenDemo={() => openDemo()} />
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} prefill={prefill} />
    </div>
  );
}

export { Header, Footer, FloatingCTAs };
